import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { 
  Palette, Save, Loader2, Eye, Upload, X, RefreshCw, Image
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { resolveFileUrl } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const COLOR_FIELDS = [
  { key: 'primary_color', label: 'Primary Color', description: 'Main brand color (logo background, links)' },
  { key: 'secondary_color', label: 'Secondary Color', description: 'Secondary brand color (CTA buttons)' },
  { key: 'accent_color', label: 'Accent Color', description: 'Accent highlights' },
  { key: 'header_bg', label: 'Header Background', description: 'Public pages header background' },
  { key: 'header_text', label: 'Header Text', description: 'Public pages header text color' },
  { key: 'page_bg', label: 'Page Background', description: 'Public pages background color' },
  { key: 'footer_bg', label: 'Footer Background', description: 'Public pages footer background' },
  { key: 'footer_text', label: 'Footer Text', description: 'Public pages footer text color' },
  { key: 'button_primary_bg', label: 'Primary Button BG', description: 'Primary button background' },
  { key: 'button_primary_text', label: 'Primary Button Text', description: 'Primary button text color' },
  { key: 'button_secondary_bg', label: 'Secondary Button BG', description: 'Secondary button background' },
  { key: 'button_secondary_text', label: 'Secondary Button Text', description: 'Secondary button text' },
];

const DEFAULT_THEME = {
  primary_color: '#2B4B6A',
  secondary_color: '#F58A5A',
  accent_color: '#FFD166',
  header_bg: '#FFFFFF',
  header_text: '#1F2937',
  page_bg: '#F9FAFB',
  footer_bg: '#2B4B6A',
  footer_text: '#FFFFFF',
  button_primary_bg: '#2B4B6A',
  button_primary_text: '#FFFFFF',
  button_secondary_bg: '#F58A5A',
  button_secondary_text: '#FFFFFF',
  logo_url: '',
  site_title: 'Trados — The Trusted Trade Platform'
};

// Logo Upload Component for Public Theme
const PublicLogoUpload = ({ currentLogoUrl, onLogoChange, uploading, setUploading }) => {
  const fileInputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(currentLogoUrl);

  useEffect(() => {
    setPreviewUrl(currentLogoUrl);
  }, [currentLogoUrl]);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await api.post('/uploads/public-logo', formData);
      const newLogoUrl = res.data.logo_url;
      setPreviewUrl(newLogoUrl);
      onLogoChange(newLogoUrl);
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = () => {
    setPreviewUrl('');
    onLogoChange('');
  };

  const getFullUrl = (url) => {
    if (!url) return null;
    return resolveFileUrl(url, API_URL);
  };

  return (
    <div className="py-3 space-y-4">
      <label className="block text-sm font-medium text-gray-700 mb-3">Platform Logo</label>
      
      <div className="flex items-start gap-4">
        <div 
          className="w-56 h-20 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center overflow-hidden p-3 bg-white"
        >
          {previewUrl ? (
            <img 
              src={getFullUrl(previewUrl)} 
              alt="Logo preview" 
              className="w-full h-auto object-contain"
            />
          ) : (
            <div className="text-center text-gray-400">
              <Image className="w-8 h-8 mx-auto mb-1" />
              <span className="text-xs">No logo</span>
            </div>
          )}
        </div>

        <div className="flex-1">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*"
            className="hidden"
          />
          
          <div className="flex flex-col gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg transition flex items-center gap-2 disabled:opacity-50 hover:bg-orange-700"
            >
              <Upload className="w-4 h-4" />
              {uploading ? 'Uploading...' : 'Upload Logo'}
            </button>
            
            {previewUrl && (
              <button
                onClick={handleRemoveLogo}
                className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Remove Logo
              </button>
            )}
          </div>
          
          <p className="text-xs text-gray-500 mt-2">
            Recommended: PNG or SVG with transparent background. Max 5MB.
          </p>
        </div>
      </div>
    </div>
  );
};

const AdminPublicTheme = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [theme, setTheme] = useState(DEFAULT_THEME);

  const isMainAdmin = user?.role === 'admin' || user?.role === 'main_admin';

  useEffect(() => {
    fetchTheme();
  }, []);

  const fetchTheme = async () => {
    try {
      const res = await api.get('/admin/settings/public-theme');
      setTheme({ ...DEFAULT_THEME, ...res.data });
    } catch (error) {
      console.error('Failed to fetch theme:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings/public-theme', theme);
      toast.success('Public page theme saved! Changes will appear immediately on login, register, and other public pages.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save theme');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Reset all colors to default values?')) {
      setTheme(DEFAULT_THEME);
      toast.info('Colors reset to defaults. Click "Save Changes" to apply.');
    }
  };

  const handleColorChange = (key, value) => {
    setTheme(prev => ({ ...prev, [key]: value }));
  };

  const handleLogoChange = (url) => {
    setTheme(prev => ({ ...prev, logo_url: url }));
  };

  if (!isMainAdmin) {
    return (
      <Sidebar>
        <div className="p-8 text-center">
          <p className="text-red-600">Access denied. Main Admin only.</p>
        </div>
      </Sidebar>
    );
  }

  if (loading) {
    return (
      <Sidebar>
        <div className="p-8 flex items-center justify-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
        </div>
      </Sidebar>
    );
  }

  const getLogoUrl = (url) => {
    if (!url) return null;
    return resolveFileUrl(url, API_URL);
  };

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Public Pages Theme</h1>
            <p className="text-gray-500 mt-1">Customize colors, logo, and branding for Login, Register, and other public pages on the main domain</p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4" />
              Reset to Defaults
            </button>
            <button
              onClick={handleSave}
              disabled={saving || uploading}
              className="flex items-center gap-2 px-5 py-2.5 bg-orange-600 text-white rounded-xl hover:bg-orange-700 disabled:opacity-50"
              data-testid="save-public-theme-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Changes
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Logo & Branding Section */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 lg:col-span-2">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Image className="w-5 h-5 text-orange-600" />
              Logo & Branding
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <PublicLogoUpload
                currentLogoUrl={theme.logo_url}
                onLogoChange={handleLogoChange}
                uploading={uploading}
                setUploading={setUploading}
              />
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Site Title</label>
                  <input
                    type="text"
                    value={theme.site_title || ''}
                    onChange={(e) => setTheme(prev => ({ ...prev, site_title: e.target.value }))}
                    placeholder="Trados — The Trusted Trade Platform"
                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Appears in the footer copyright text on public pages.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Color Settings */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Palette className="w-5 h-5 text-orange-600" />
              Color Settings
            </h2>
            
            <div className="grid grid-cols-2 gap-4">
              {COLOR_FIELDS.map(field => (
                <div key={field.key} className="group">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-10 h-10 rounded-lg border border-gray-200 flex-shrink-0 cursor-pointer relative overflow-hidden"
                      style={{ backgroundColor: theme[field.key] }}
                    >
                      <input
                        type="color"
                        value={theme[field.key]}
                        onChange={(e) => handleColorChange(field.key, e.target.value)}
                        className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                      />
                    </div>
                    <input
                      type="text"
                      value={theme[field.key]}
                      onChange={(e) => handleColorChange(field.key, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    {field.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Live Preview */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-600" />
              Live Preview
            </h2>
            
            {/* Preview Container */}
            <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
              {/* Header Preview */}
              <div 
                className="px-4 py-3 flex items-center justify-between"
                style={{ backgroundColor: theme.header_bg }}
              >
                <div className="flex items-center gap-2">
                  {theme.logo_url ? (
                    <img 
                      src={getLogoUrl(theme.logo_url)} 
                      alt="Logo" 
                      className="h-10 w-auto object-contain"
                    />
                  ) : (
                    <>
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: theme.primary_color }}
                      >
                        <span className="text-white font-bold text-sm">T</span>
                      </div>
                      <span className="font-semibold" style={{ color: theme.header_text }}>Trados</span>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm px-3 py-1" style={{ color: theme.header_text }}>Login</span>
                  <span 
                    className="text-sm px-3 py-1.5 rounded-lg text-white"
                    style={{ backgroundColor: theme.secondary_color }}
                  >
                    Get Started
                  </span>
                </div>
              </div>
              
              {/* Page Content Preview */}
              <div 
                className="p-6"
                style={{ backgroundColor: theme.page_bg }}
              >
                <div className="max-w-xs mx-auto bg-white rounded-xl p-4 shadow-sm">
                  <h3 className="font-semibold text-center mb-3" style={{ color: theme.primary_color }}>
                    Welcome Back
                  </h3>
                  <div className="space-y-2">
                    <input 
                      type="text" 
                      placeholder="Email" 
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      readOnly
                    />
                    <input 
                      type="password" 
                      placeholder="Password" 
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      readOnly
                    />
                    <button 
                      className="w-full py-2 rounded-lg text-sm font-medium"
                      style={{ 
                        backgroundColor: theme.button_primary_bg, 
                        color: theme.button_primary_text 
                      }}
                    >
                      Sign In
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Footer Preview */}
              <div 
                className="px-4 py-3 flex items-center justify-between text-sm"
                style={{ backgroundColor: theme.footer_bg, color: theme.footer_text }}
              >
                <div className="flex items-center gap-2">
                  {theme.logo_url ? (
                    <img 
                      src={getLogoUrl(theme.logo_url)} 
                      alt="Logo" 
                      className="h-6 w-auto object-contain"
                    />
                  ) : (
                    <>
                      <div 
                        className="w-5 h-5 rounded flex items-center justify-center"
                        style={{ backgroundColor: theme.primary_color }}
                      >
                        <span className="text-white text-xs font-bold">T</span>
                      </div>
                      <span>Trados</span>
                    </>
                  )}
                </div>
                <span>© 2025 {theme.site_title || 'Trados'}</span>
              </div>
            </div>
            
            <p className="text-xs text-gray-500 mt-4 text-center">
              This preview shows how Login, Register, and Forgot Password pages will look
            </p>
          </div>
        </div>

        {/* Info Box */}
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <h3 className="font-medium text-blue-800 mb-2">Where these settings apply:</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• <strong>Main domain pages only</strong> — Login, Register, Forgot Password, Landing page</li>
            <li>• <strong>Company subdomain pages</strong> use their own theme settings configured in Company Settings</li>
            <li>• Changes apply immediately after saving — no restart required</li>
          </ul>
        </div>
      </div>
    </Sidebar>
  );
};

export default AdminPublicTheme;
